import React, { useState, useEffect } from 'react';
import { Save, Store, MessageCircle, CreditCard, Loader2, Megaphone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { settingsAPI } from '../../services/api';

const SettingsManager = () => {
  const [settings, setSettings] = useState({
    storeName: 'Forged Supps',
    tagline: 'Premium Performance Products',
    currency: 'GBP',
    currencySymbol: '£',
    minOrder: 100,
    whatsappNumber: '447440352811',
    email: 'support@forgedsupps.com',
    bannerEnabled: true,
    bannerText: 'Free UK Delivery on Orders Over £100',
    bannerColor: '#f59e0b'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await settingsAPI.get();
        setSettings(prev => ({ ...prev, ...response.data }));
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-zinc-400">Configure your store settings</p>
      </div>

      {/* Banner Settings */}
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-amber-500/10 rounded-lg p-2">
            <Megaphone className="h-5 w-5 text-amber-500" />
          </div>
          <h3 className="text-lg font-semibold text-white">Banner Bar</h3>
        </div>

        <div className="space-y-6">
          {/* Banner On/Off */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-white">Enable Banner</Label>
              <p className="text-zinc-500 text-sm">Show the scrolling banner at the top of the site</p>
            </div>
            <Switch
              checked={settings.bannerEnabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, bannerEnabled: checked }))}
            />
          </div>

          {/* Banner Text */}
          <div>
            <Label className="text-white">Banner Text</Label>
            <Input
              name="bannerText"
              value={settings.bannerText || ''}
              onChange={handleChange}
              placeholder="Free UK Delivery on Orders Over £100"
              className="mt-2 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>

          {/* Banner Color */}
          <div>
            <Label className="text-white">Banner Color</Label>
            <div className="flex items-center gap-4 mt-2">
              <input
                type="color"
                value={settings.bannerColor || '#f59e0b'}
                onChange={(e) => setSettings(prev => ({ ...prev, bannerColor: e.target.value }))}
                className="w-12 h-10 rounded cursor-pointer border border-zinc-700"
              />
              <Input
                name="bannerColor"
                value={settings.bannerColor || '#f59e0b'}
                onChange={handleChange}
                placeholder="#f59e0b"
                className="flex-1 bg-zinc-800 border-zinc-700 text-white font-mono"
              />
              {/* Preset colors */}
              <div className="flex gap-2">
                {['#f59e0b', '#ef4444', '#22c55e', '#3b82f6', '#8b5cf6'].map((color) => (
                  <button
                    key={color}
                    onClick={() => setSettings(prev => ({ ...prev, bannerColor: color }))}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      settings.bannerColor === color ? 'border-white scale-110' : 'border-zinc-700'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Preview */}
          {settings.bannerEnabled && (
            <div>
              <Label className="text-white mb-2 block">Preview</Label>
              <div 
                className="py-2 px-4 rounded-lg text-center font-medium"
                style={{ 
                  backgroundColor: settings.bannerColor,
                  color: isLightColor(settings.bannerColor) ? '#000' : '#fff'
                }}
              >
                🔥 {settings.bannerText || 'Banner text here'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Store Information */}
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-amber-500/10 rounded-lg p-2">
            <Store className="h-5 w-5 text-amber-500" />
          </div>
          <h3 className="text-lg font-semibold text-white">Store Information</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="text-white">Store Name</Label>
            <Input
              name="storeName"
              value={settings.storeName}
              onChange={handleChange}
              className="mt-2 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          <div>
            <Label className="text-white">Tagline</Label>
            <Input
              name="tagline"
              value={settings.tagline}
              onChange={handleChange}
              className="mt-2 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
        </div>
      </div>

      {/* Contact Settings */}
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-green-500/10 rounded-lg p-2">
            <MessageCircle className="h-5 w-5 text-green-500" />
          </div>
          <h3 className="text-lg font-semibold text-white">Contact Settings</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="text-white">WhatsApp Number</Label>
            <Input
              name="whatsappNumber"
              value={settings.whatsappNumber}
              onChange={handleChange}
              placeholder="447440352811"
              className="mt-2 bg-zinc-800 border-zinc-700 text-white"
            />
            <p className="text-zinc-500 text-sm mt-1">Without + or spaces (e.g., 447440352811)</p>
          </div>
          <div>
            <Label className="text-white">Support Email</Label>
            <Input
              name="email"
              value={settings.email || ''}
              onChange={handleChange}
              placeholder="support@forgedsupps.com"
              className="mt-2 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
        </div>
      </div>

      {/* Order Settings */}
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-blue-500/10 rounded-lg p-2">
            <CreditCard className="h-5 w-5 text-blue-500" />
          </div>
          <h3 className="text-lg font-semibold text-white">Order Settings</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="text-white">Minimum Order (£)</Label>
            <Input
              name="minOrder"
              type="number"
              value={settings.minOrder}
              onChange={handleChange}
              className="mt-2 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          <div>
            <Label className="text-white">Currency Symbol</Label>
            <Input
              name="currencySymbol"
              value={settings.currencySymbol}
              onChange={handleChange}
              className="mt-2 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className={`${
            saved 
              ? 'bg-green-600 hover:bg-green-600' 
              : 'bg-amber-500 hover:bg-amber-600'
          } text-black font-semibold px-8`}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {saved ? 'Saved!' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

// Helper function to determine if a color is light or dark
function isLightColor(color) {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128;
}

export default SettingsManager;
